import React, { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNavigation,
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { DeleteButton } from "@/components/refine-ui/buttons/delete";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { getDirtyValues, isFormDirty } from "@/lib/utils";
import { parseRead } from "@/lib/zod/read-schema";
import {
  CertificationsField,
  ReleaseDatesField,
} from "@/pages/movies/components/fields/country-map-field";
import { MovieCastField } from "@/pages/movies/components/fields/movie-cast-field";
import { ShowGenresField } from "@/pages/movies/components/fields/show-genres-field";
import { useMovieRepository } from "@/pages/movies/hooks/use-movie-repository";
import {
  MovieEditFormType,
  movieEditSchema,
} from "@/pages/movies/schemas/movie.edit.form";

const schemaProvider = new ZodProvider(movieEditSchema);

export const MoviesEdit = () => {
  const { get, update } = useMovieRepository();
  const { id } = useParsed();
  const [values, setValues] = React.useState<MovieEditFormType>();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();
  const { list } = useNavigation();
  const setTitle = useDocumentTitle();

  useEffect(() => {
    if (!id) {
      open?.({
        type: "error",
        message: "Something went wrong. This shouldn't be happening",
      });
      return;
    }
    setLoading(true);
    get(id.toString())
      .then((response) => {
        // Lenient read. The hard `.parse()` this replaces made every
        // field a tripwire for the whole page: one `enrichmentStatus` the enum
        // did not know, and /movies/edit/:id was an empty form plus a "Failed
        // to get movie" toast that named nothing.
        const data = parseRead(movieEditSchema, response, "movie");
        setValues(data);
        setTitle("Edit " + data.title);
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get movie",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const initializeForm = (
    form: UseFormReturn<MovieEditFormType, any, MovieEditFormType>,
  ) => {
    form.watch((value) => {
      setWarnWhen(
        form.formState.isDirty &&
          isFormDirty(
            form.formState.dirtyFields as Record<string, unknown>,
            value as Record<string, unknown>,
            form.formState.defaultValues as Record<string, unknown>,
          ),
      );
    });
  };

  const onSubmit = async (
    allValues: MovieEditFormType,
    form: UseFormReturn<MovieEditFormType, any, MovieEditFormType>,
  ) => {
    const dirty = getDirtyValues(
      form.formState.dirtyFields as Record<string, unknown>,
      allValues,
    );
    setWarnWhen(false);
    await update({ id: allValues.id, ...dirty });
  };

  return (
    <div className="flex flex-row">
      <div className="flex w-full flex-col space-y-4 p-8">
        {values && (
          <AutoForm
            schema={schemaProvider}
            defaultValues={values}
            onSubmit={onSubmit}
            onFormInit={initializeForm}
            {...injectCustomAutoFormComponents({
              formComponents: {
                showGenres: ShowGenresField,
                movieCast: MovieCastField,
                certifications: CertificationsField,
                releaseDates: ReleaseDatesField,
              },
            })}
          >
            <BottomActionBar className="justify-between">
              <Button disabled={!warnWhen} type="submit">
                Submit
              </Button>
              <DeleteButton
                resource="movies"
                id={id?.toString()}
                onSuccess={() => list("movies")}
              />
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
    </div>
  );
};
